import { useEffect, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { rtdb } from '../../../firebase';

// Helper function to sanitize email for RTDB key
const sanitizeEmailForKey = (email) => {
  if (!email || typeof email !== 'string') return '';
  return email.toLowerCase().replace(/[.#$[\]]/g, '_');
};

export default function useUserOnlineStatus(userIdentifier) {
  const [online, setOnline] = useState(false);
  const [lastSeen, setLastSeen] = useState(null);
  
  useEffect(() => {
    if (!userIdentifier) {
      setOnline(false);
      setLastSeen(null);
      return;
    }

    // Handle different input types
    let uid = null;
    let email = null;
    
    if (typeof userIdentifier === 'string') {
      // If it's a string, check if it looks like an email or UID
      if (userIdentifier.includes('@')) {
        email = userIdentifier;
      } else {
        uid = userIdentifier;
      }
    } else if (typeof userIdentifier === 'object' && userIdentifier !== null) {
      // If it's an object, extract uid and email
      uid = userIdentifier.uid || userIdentifier.id || null;
      email = userIdentifier.email || null;
    }

    if (!uid && !email) {
      setOnline(false);
      setLastSeen(null);
      return;
    }

    // Create references for both UID and email-based presence
    const uidStatusRef = uid ? ref(rtdb, `status/${uid}`) : null;
    const emailStatusRef = email ? ref(rtdb, `statusByEmail/${sanitizeEmailForKey(email)}`) : null;

    // Track if we've received any response
    let hasReceivedResponse = false;
    let uidOnline = false;
    let uidLastSeen = null;
    let emailOnline = false;
    let emailLastSeen = null;

    const updateStatus = () => {
      if (hasReceivedResponse) {
        // If any source reports true, user is online
        const isOnline = uidOnline || emailOnline;
        setOnline(isOnline);
        
        // Use the most recent lastSeen timestamp
        const lastSeenTimestamp = Math.max(
          uidLastSeen || 0,
          emailLastSeen || 0
        );
        setLastSeen(lastSeenTimestamp > 0 ? lastSeenTimestamp : null);
        
      }
    };

    // Set up UID-based presence listener
    let uidUnsubscribe = null;
    if (uidStatusRef) {
      uidUnsubscribe = onValue(uidStatusRef, (snapshot) => {
        hasReceivedResponse = true;
        const data = snapshot.val();
        if (data) {
          uidOnline = data.online === true;
          uidLastSeen = data.lastSeen || null;
        } else {
          uidOnline = false;
          uidLastSeen = null;
        }
        updateStatus();
      }, (error) => {
        hasReceivedResponse = true;
        uidOnline = false;
        uidLastSeen = null;
        updateStatus();
      });
    }

    // Set up email-based presence listener
    let emailUnsubscribe = null;
    if (emailStatusRef) {
      emailUnsubscribe = onValue(emailStatusRef, (snapshot) => {
        hasReceivedResponse = true;
        const data = snapshot.val();
        if (data) {
          emailOnline = data.online === true;
          emailLastSeen = data.lastSeen || null;
        } else {
          emailOnline = false;
          emailLastSeen = null;
        }
        updateStatus();
      }, (error) => {
        hasReceivedResponse = true;
        emailOnline = false;
        emailLastSeen = null;
        updateStatus();
      });
    }

    // If no listeners were set up, mark as offline
    if (!uidUnsubscribe && !emailUnsubscribe) {
      setOnline(false);
      setLastSeen(null);
    }

    return () => {
      if (uidUnsubscribe) uidUnsubscribe();
      if (emailUnsubscribe) emailUnsubscribe();
    };
  }, [userIdentifier]);
  
  return { online, lastSeen };
} 